import React, { useState, useMemo, useEffect } from "react";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";

import { AxiosResponse } from "axios";
import { PageTitle } from "components/common/Text";
import { PrimaryButton } from "components/common/Buttons";
import { TableBodyCell } from "components/table/table.style";
import { UserColumnTypes } from "pages/configs/configs";
import { ActionBar } from "components/common/Wrapper";
import { getCurrentPage, getFilteredListNew, getSortList } from "utils/util-functions";
import { getSupportUserInfo } from "api/PowerUserHelpers";
import CustomTable from "components/table/table.component";
import { useAuth } from "hooks/useProvideAuth";
import TableFilterNew from "components/table/TableFilterNew.component";
import { SUPPORT_USERNAME_PREFIX } from "utils/constants";
import useOrganizations from "hooks/useOrganizations";
import { ORG_COLUMNS, SUPPORT_USER_COLUMNS } from "./Support.config";
import { AdminListTypes } from "../Admins/Admins.config";
import { ORG_COLUMN_TYPES, OrgListDetailTypes } from "../Organizations/Organizations.config";

const SupportPage = (): JSX.Element => {
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [filterColumn, setFilterColumn] = useState<string>("");
  const [filterWord, setFilterWord] = useState<string>("");
  const [filterType, setFilterType] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("");
  const [supportList, setSupportList] = useState<AdminListTypes[]>([]);

  const { sub: userId }: Record<string, string> = useAuth();

  const [reloadOrg, setReloadOrg] = useState<boolean>(true);
  const { isLoading, orgList } = useOrganizations(userId, reloadOrg);

  useEffect(() => {
    if (!isLoading) {
      setReloadOrg(false);
    }
  }, [isLoading]);

  const supportUserColumns = useMemo(() => {
    const tempCols = [...SUPPORT_USER_COLUMNS];
    tempCols.pop();
    return tempCols;
  }, []);

  const updateSupportList = (data: AdminListTypes[]) => {
    const list = data.map((x: AdminListTypes) => {
      const attributes: Record<string, string> = {};
      x.Attributes.forEach((attr) => {
        attributes[attr.Name] = attr.Value;
      });
      return {
        ...x,
        ...attributes,
        status: attributes.profile === "None" ? "" : x.Enabled ? "Granted" : "Pending",
      };
    });

    setSupportList(list);
  };

  const fetchSupportUser = async () => {
    const { data } = await getSupportUserInfo({
      username: userId,
      supportname: SUPPORT_USERNAME_PREFIX,
    });
    updateSupportList(data);
  };

  const handleRequestAccess = async (event: never, row: OrgListDetailTypes) => {
    const idleUser = supportList.find((u) => u.profile === "None");
    if (!idleUser) return;

    const { data }: AxiosResponse<any, any> = await getSupportUserInfo({
      username: userId,
      supportname: SUPPORT_USERNAME_PREFIX,
      newgroupname: row.GroupName,
      oldgroupname: idleUser.profile,
      email: idleUser.email,
    });

    updateSupportList(data);
  };

  const handleReset = async (row: AdminListTypes) => {
    const { data }: AxiosResponse<any, any> = await getSupportUserInfo({
      username: userId,
      supportname: SUPPORT_USERNAME_PREFIX,
      action: "reset",
      groupname: row.profile,
      email: row.email,
    });

    updateSupportList(data);
  };

  useEffect(() => {
    fetchSupportUser();
  }, []);

  const filteredList = useMemo(
    () => getFilteredListNew(orgList, filterColumn, filterType, filterWord),
    [orgList, filterColumn, filterWord, filterType],
  );
  const sortedList = useMemo(() => {
    const sortColumn = ORG_COLUMNS.find(({ id }) => id === sortBy);
    return getSortList(filteredList, sortBy, sortOrder, sortColumn?.type);
  }, [sortBy, sortOrder, filteredList]);
  const paginatedList = useMemo(() => getCurrentPage(sortedList, page, rowsPerPage), [sortedList, page, rowsPerPage]);
  const totalOrgCount = useMemo(() => filteredList?.length || 0, [filteredList]);

  const filterColumns = ORG_COLUMNS.filter(({ filter }) => filter);

  const orgColumns = useMemo(() => {
    const tempCols = [...ORG_COLUMNS];
    tempCols.pop();
    return tempCols;
  }, []);

  const onSearch = (column: string, columnType: string, searchContent: string) => {
    setFilterColumn(column);
    setFilterWord(searchContent);
    setFilterType(columnType);
    setPage(0);
  };

  const onResetFilter = () => {
    setFilterColumn("");
    setFilterWord("");
    setFilterType("");
    setPage(0);
  };

  const handleRequestSort = (order: string, orderBy: string) => {
    setSortOrder(order);
    setSortBy(orderBy);
  };

  return (
    <>
      <PageTitle>Support access to organization</PageTitle>
      <CustomTable boxShadow>
        <CustomTable.Header columns={SUPPORT_USER_COLUMNS} onRequestSort={handleRequestSort} />

        <CustomTable.Body isLoading={!supportList.length} deviceData={supportList}>
          {supportList.map(
            (row: AdminListTypes) =>
              row && (
                <TableRow key={row.Username}>
                  {supportUserColumns.map((column: UserColumnTypes) => {
                    const value = row[column.id as keyof AdminListTypes];

                    return (
                      <TableBodyCell key={column.id}>{column.format ? column.format(value) : value}</TableBodyCell>
                    );
                  })}

                  <TableBodyCell style={{ width: 120 }}>
                    {row.profile !== "None" && (
                      <PrimaryButton color="error" onClick={() => handleReset(row)}>
                        Reset
                      </PrimaryButton>
                    )}
                  </TableBodyCell>
                </TableRow>
              ),
          )}
        </CustomTable.Body>
      </CustomTable>

      <Box sx={{ height: 50 }} />
      <PageTitle>Organizations</PageTitle>

      <ActionBar style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        {orgList?.length > 5 ? (
          <TableFilterNew columns={filterColumns} onSearch={onSearch} onReset={onResetFilter} />
        ) : (
          <div />
        )}
      </ActionBar>

      <CustomTable
        rowsPerPageOptions={[10, 25, 100]}
        totalCount={totalOrgCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(pageNum) => setPage(pageNum)}
        onRowsPerPageChange={(rowPerPage) => setRowsPerPage(rowPerPage)}
      >
        <CustomTable.Header columns={ORG_COLUMNS} onRequestSort={handleRequestSort} />

        <CustomTable.Body isLoading={isLoading} deviceData={paginatedList}>
          {paginatedList &&
            paginatedList.map(
              (row: OrgListDetailTypes) =>
                row && (
                  <TableRow key={row.GroupName}>
                    {orgColumns.map((column: ORG_COLUMN_TYPES) => (
                      <TableBodyCell key={column.id}>
                        {column.format
                          ? column.format(row[column.id as keyof OrgListDetailTypes])
                          : row[column.id as keyof OrgListDetailTypes]}
                      </TableBodyCell>
                    ))}
                    <TableBodyCell>
                      <PrimaryButton
                        onClick={(e: never) => handleRequestAccess(e, row)}
                        variant="contained"
                        disabled={!supportList.some((u) => u.profile === "None")}
                      >
                        Request
                      </PrimaryButton>
                    </TableBodyCell>
                  </TableRow>
                ),
            )}
        </CustomTable.Body>
      </CustomTable>
    </>
  );
};

export default SupportPage;
